import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'

import { useAccessToken } from 'store'
import { useLocalStorageClear } from 'utils'

const apiUrl = process.env.REACT_APP_API_URL
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const authAxiosInstance: AxiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    Accept: 'application/json',
    'X-User-Timezone': userTimezone
  }
})

const onAuthRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const token = useAccessToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else {
    delete config.headers.Authorization
  }

  return config
}

const onAuthResponse = (response: AxiosResponse): AxiosResponse => response

const onAuthRequestError = async (
  error: AxiosError
): Promise<AxiosError> => await Promise.reject(error)

const killSession = () => {
  useLocalStorageClear()
  window.location.href = '/'
}

const onAuthResponseError = async (
  error: AxiosError
): Promise<AxiosError> => {
  if (error.response?.status === 401 && useAccessToken()) {
    killSession()
  }

  return await Promise.reject(error)
}

authAxiosInstance.interceptors.request.use(
  onAuthRequest,
  onAuthRequestError
)

authAxiosInstance.interceptors.response.use(
  onAuthResponse,
  onAuthResponseError
)

export default authAxiosInstance

export interface AuthResponse<T> {
  code: number
  data: T
}

export const setSMSVerificationCode = async (phone: string) => {
  const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`

  const { data } = await authAxiosInstance.post(
    'api/send-sms-code',
    { phone: formattedPhone }
  )

  return data
}

export const getSMSVerificationCode = async ({
  otp,
  reference
}: {
  otp: string
  reference: string
}) => {
  const { data } = await authAxiosInstance.post(
    'api/verify-sms-code',
    { otp, reference }
  )

  return data
}

export const getQRCode = async () => {
  const { data } = await authAxiosInstance.post(
    'api/2fa/setup'
  )

  return data
}

export const setQRCode = async (otp: string) => {
  const { data } = await authAxiosInstance.post(
    'api/2fa/enable',
    { otp }
  )

  return data
}

export const getOtpSMS = async () => {
  const { data } = await authAxiosInstance.post(
    'api/resend-sms-code',
    {}
  )

  return data
}

export const verifyLoginTwoFactor = async ({
  challenge,
  otp,
  remember_device
}: {
  challenge: string
  otp: string
  remember_device: boolean
}) => {
  const { data } = await authAxiosInstance.post(
    'api/login/2fa/verify',
    {
      challenge,
      otp,
      remember_device
    }
  )

  return data?.data ?? data
}
export const resendLoginTwoFactor = async ({
  challenge
}: {
  challenge: string
}) => {
  const { data } = await authAxiosInstance.post(
    'api/login/2fa/resend',
    {
      challenge
    }
  )

  return data
}